import { cn } from '@/lib/utils';

type StickyScrollFadeProps = {
  /** Whether the fade is visible — pass `useScrollFade(direction)`'s `showFade`. */
  show: boolean;
  /**
   * Which edge of the sticky element to fade, matching `useScrollFade`:
   * - `top` (default): fade below a top-pinned bar so content melts under it.
   * - `bottom`: fade above a bottom-pinned element (e.g. a sticky footer).
   */
  direction?: 'top' | 'bottom';
  /**
   * Tailwind `from-*` class for the gradient's solid end — the surface color the
   * content fades into. Must be a full class literal so Tailwind detects it.
   */
  color?: string;
  className?: string;
};

/**
 * Gradient overlay for the scrolling edge of a sticky element, so content
 * scrolling past it melts into the element instead of being hard-cut. Render it
 * as the sticky element's first child (keeps any `[&>*:last-child]` rule
 * targeting the real last child) and drive `show` from `useScrollFade`.
 */
export const StickyScrollFade = ({
  show,
  direction = 'top',
  color = 'from-bg-muted-light',
  className,
}: StickyScrollFadeProps) => (
  <div
    aria-hidden
    className={cn(
      'pointer-events-none absolute inset-x-0 h-4 from-20% to-transparent transition-opacity duration-200',
      color,
      direction === 'top' ? 'top-full bg-gradient-to-b' : 'bottom-full bg-gradient-to-t',
      show ? 'opacity-100' : 'opacity-0',
      className
    )}
  />
);
